import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query"
import {
	ErrorResponse,
	SuccessPageResponse,
	SuccessResponse,
	useHttpClient,
} from "api/api"
import {
	AdminCareOrganization,
	AdminCareOrganizationCreatePayload,
	AdminCareOrganizationProvider,
	AdminCareOrganizationProviderPermissionPayload,
	AdminCareOrganizationUpdatePayload,
} from "interface/careOrganization"
import { Organization } from "interface/user"

const LIMIT = 20

export const queryKeys = {
	list: "admin-org-list",
	adminList: "admin-care-organization-list",
	adminCreate: "admin-care-organization-create",
	adminUpdate: "admin-care-organization-update",
	adminProviders: "admin-care-organization-providers",
	adminProviderAdd: "admin-care-organization-provider-add",
	adminProviderUpdate: "admin-care-organization-provider-update",
}

export const useAdminOrganizations = () => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: [queryKeys.list],
		queryFn: async ({ signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessPageResponse<Organization>>(
				"/api/v1/admin/care-organizations",
				{
					signal,
					params: {
						sort: ["name-ascending"],
					},
				},
			)

			return data
		},
		staleTime: 3_600_000, // 1 hour
		select({ result }) {
			return result
		},
	})
}

export const useAdminCareOrganizations = ({
	sort,
	query,
	page = "1",
	excludedFromAnalytics,
}: {
	sort: string[]
	query?: string
	page?: string
	excludedFromAnalytics?: boolean
}) => {
	const { httpClient } = useHttpClient()
	const params = {
		limit: LIMIT,
		page,
		sort,
		query: query?.trim() || undefined,
		excludedFromAnalytics,
	}

	return useQuery({
		queryKey: [queryKeys.adminList, params],
		queryFn: async ({ signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessPageResponse<AdminCareOrganization>>(
				"/api/v1/admin/care-organizations/list",
				{
					signal,
					params,
				},
			)

			return data
		},
		placeholderData: keepPreviousData,
	})
}

export const useAdminCareOrganizationCreate = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<void, ErrorResponse, AdminCareOrganizationCreatePayload>({
		mutationKey: [queryKeys.adminCreate],
		mutationFn: (payload) =>
			httpClient.post("/api/v1/admin/care-organizations", payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [queryKeys.adminList] })
			queryClient.invalidateQueries({ queryKey: [queryKeys.list] })
		},
	})
}

export const useAdminCareOrganizationUpdate = (id: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<void, ErrorResponse, AdminCareOrganizationUpdatePayload>({
		mutationKey: [queryKeys.adminUpdate, id],
		mutationFn: (payload) =>
			httpClient.patch(`/api/v1/admin/care-organizations/${id}`, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [queryKeys.adminList] })
			queryClient.invalidateQueries({ queryKey: [queryKeys.list] })
		},
	})
}

export const useAdminCareOrganizationProviders = (id: string) => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: [queryKeys.adminProviders, id],
		queryFn: async ({ signal }) => {
			const {
				data: { data },
			} = await httpClient.get<
				SuccessResponse<AdminCareOrganizationProvider[]>
			>(`/api/v1/admin/care-organizations/${id}/providers`, { signal })

			return data
		},
	})
}

export const useAdminCareOrganizationProviderAdd = (id: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<void, ErrorResponse, { userId: string }>({
		mutationKey: [queryKeys.adminProviderAdd, id],
		mutationFn: ({ userId }) =>
			httpClient.post(
				`/api/v1/admin/care-organizations/${id}/providers/${userId}`,
			),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.adminProviders, id],
			})
		},
	})
}

export const useAdminCareOrganizationProviderPermissionUpdate = (
	id: string,
	userId: string,
) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<
		void,
		ErrorResponse,
		AdminCareOrganizationProviderPermissionPayload
	>({
		mutationKey: [queryKeys.adminProviderUpdate, id, userId],
		mutationFn: (payload) =>
			httpClient.patch(
				`/api/v1/admin/care-organizations/${id}/providers/${userId}`,
				payload,
			),
		onSuccess: (_, payload) => {
			// Patch the cache first so the row doesn't flash its old values while refetching
			queryClient.setQueryData<AdminCareOrganizationProvider[]>(
				[queryKeys.adminProviders, id],
				(providers) =>
					providers?.map((provider) =>
						provider.id === userId ? { ...provider, ...payload } : provider,
					),
			)
			queryClient.invalidateQueries({
				queryKey: [queryKeys.adminProviders, id],
			})
		},
	})
}
